import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Task } from "../types";
import { useStore } from "../../../helpers";
import { taskQueryKeys } from "../utils/queryKeys";

interface UseAssignTaskOptions {
  task: Task;
  onSuccess?: () => void;
}

export const useAssignTask = ({ task, onSuccess }: UseAssignTaskOptions) => {
  const { taskManagerStore } = useStore();
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);

  const assignTaskMutation = useMutation(
    async ({ userId, interviewCount }: { userId: number; interviewCount: number }) => {
      const updatedTask = {
        ...task,
        userid: userId,
        user: taskManagerStore.users.find((u) => u.id === userId) || task.user,
        count: {
          ...task.count,
          count: interviewCount,
        },
      };
      taskManagerStore.set_active_task(updatedTask);
      await taskManagerStore.update_task();
    },
    {
      onSuccess: () => {
        toast.success("✓ Task assigned successfully");
        queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
        queryClient.invalidateQueries({ queryKey: taskQueryKeys.detail(task.id) });
        setShowAssignModal(false);
        onSuccess?.();
      },
      onError: () => {
        toast.error("Failed to assign task");
      },
    }
  );

  const handleAssign = useCallback(
    (userId: number, interviewCount: number) => {
      assignTaskMutation.mutate({ userId, interviewCount });
    },
    [assignTaskMutation]
  );

  const handleAssignClick = useCallback(() => {
    setShowAssignModal(true);
  }, []);

  return {
    showAssignModal,
    setShowAssignModal,
    handleAssign,
    handleAssignClick,
    isAssigning: assignTaskMutation.isLoading,
  };
};

